<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore, useNotificationStore } from '@/stores'
import { useGoogleTranslate } from '@/composables/useGoogleTranslate'
import { useCurrency } from '@/composables/useCurrency'
// import FirebaseChatWidget from '@/components/chat/FirebaseChatWidget.vue'
import api from '@/services/api'
import logoImg from '@/assets/logo.png'

interface NavChild {
  to: string
  label: string
}

interface NavItem {
  key: string
  label: string
  icon: string
  to?: string
  /** Paths that belong to this item, for the "you are here" marker */
  match: string[]
  children?: NavChild[]
  /** "All …" link shown above the children */
  allLink?: NavChild
  wide?: boolean
}

const NOTIFICATION_PREVIEW = 5

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const router = useRouter()
const route = useRoute()

// Google Translate
const { currentLanguage, translatorReady, initialize: initTranslator, changeLanguage } = useGoogleTranslate({
  pageLanguage: 'en',
  includedLanguages: 'en,bn'
})

const sidebarOpen = ref(false)
const activeSubmenu = ref<string | null>(null)
const openMenu = ref<string | null>(null)
const notificationPopupOpen = ref(false)
const profileDropdownOpen = ref(false)
const searchQuery = ref('')
const drawerClose = ref<HTMLButtonElement | null>(null)
const menuToggle = ref<HTMLButtonElement | null>(null)

// Countries for the menus
const packageCountries = ref<Array<{ id: number; name: string }>>([])
const visaCountries = ref<Array<{ id: number; name: string }>>([])
const transportCountries = ref<Array<{ id: number; name: string }>>([])
// Cities with active packages, per package country (Packages menu)
const packageCities = ref<Record<number, Array<{ id: number; name: string }>>>({})

// Packages menu: one link per city; the country name is added when there is more than one country
const packageCityLinks = computed<NavChild[]>(() =>
  packageCountries.value.flatMap(country =>
    (packageCities.value[country.id] || []).map(city => ({
      to: `/packages?country_id=${country.id}&city_ids=${city.id}`,
      label: packageCountries.value.length > 1 ? `${city.name}, ${country.name}` : city.name,
    }))
  )
)

const availableBalance = ref(0)
const { formatAmount } = useCurrency()

const loadingBalance = ref(false)
const loadingCountries = ref(false)
const loadingLanguage = ref(false)

const userName = computed(() => authStore.user?.name || 'User')
const companyName = computed(() => authStore.user?.company?.name || '')
const companyCode = computed(() => authStore.user?.company?.code || '')
const initials = computed(() =>
  userName.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'U'
)

const NAV = computed<NavItem[]>(() => [
  { key: 'home', label: 'Dashboard', icon: 'fa fa-th-large', to: '/home', match: ['/home'] },
  {
    key: 'packages',
    label: 'Packages',
    icon: 'fa fa-suitcase-rolling',
    match: ['/packages', '/package'],
    allLink: { to: '/packages', label: 'All packages' },
    children: packageCityLinks.value,
    wide: packageCityLinks.value.length > 8,
  },
  // Transport and Visa: switched off, kept for later
  // {
  //   key: 'transport', label: 'Transport', icon: 'fa fa-bus', match: ['/transports'],
  //   allLink: { to: '/transports', label: 'All transports' },
  //   children: transportCountries.value.map(country => ({ to: `/transports?country_id=${country.id}`, label: country.name })),
  // },
  // {
  //   key: 'visa', label: 'Visa', icon: 'fa fa-id-card', match: ['/visa'],
  //   allLink: { to: '/visa', label: 'All visas' },
  //   children: visaCountries.value.map(country => ({ to: `/visa?country_id=${country.id}`, label: country.name })),
  // },
  { key: 'requests', label: 'Package requests', icon: 'fa fa-clipboard-list', to: '/package-requests', match: ['/package-requests'] },
  {
    key: 'vouchers',
    label: 'Vouchers',
    icon: 'fa fa-ticket-alt',
    match: ['/booking-vouchers', '/hotel-vouchers'],
    children: [
      { to: '/booking-vouchers?type=tour_package', label: 'Package vouchers' },
      { to: '/booking-vouchers?type=sightseeing', label: 'Sightseeing vouchers' },
      { to: '/booking-vouchers?type=transport', label: 'Transport vouchers' },
      { to: '/hotel-vouchers', label: 'Hotel vouchers' },
    ],
  },
  { key: 'invoices', label: 'Invoices', icon: 'fa fa-file-invoice', to: '/invoices', match: ['/invoices'] },
  {
    key: 'finance',
    label: 'Finance',
    icon: 'fa fa-chart-line',
    match: ['/finance'],
    children: [
      { to: '/finance', label: 'Overview' },
      ...(authStore.canAccess('balance_request') ? [{ to: '/finance/balance-requests/create', label: 'Add balance' }] : []),
      { to: '/finance/balance-requests', label: 'Payment history' },
      { to: '/finance/ledger', label: 'Ledger' },
      { to: '/finance/refunds', label: 'Refund history' },
    ],
  },
  // Visa status: switched off, kept for later
  // { key: 'passports', label: 'Visa status', icon: 'fa fa-passport', to: '/passports', match: ['/passports'] },
])

const ACCOUNT_LINKS = computed(() => [
  { to: '/company/profile', label: 'Company profile', icon: 'fa fa-building', show: true },
  { to: '/users', label: 'Users', icon: 'fa fa-users', show: authStore.canAccess('users') },
  { to: '/change-password', label: 'Change password', icon: 'fa fa-key', show: true },
  { to: '/notification-settings', label: 'Notification settings', icon: 'fa fa-sliders-h', show: authStore.canAccess('notification_settings') },
].filter(link => link.show))

const latestNotifications = computed(() => notificationStore.notifications.slice(0, NOTIFICATION_PREVIEW))

// Highlight the nav item that owns the current route
function isActive(...prefixes: string[]) {
  return prefixes.some(prefix => route.path === prefix || route.path.startsWith(`${prefix}/`))
}

// Notification text arrives as HTML; the preview shows it as plain text
function preview(body: string | null): string {
  const text = new DOMParser().parseFromString(body || '', 'text/html').body.textContent || ''
  return text.replace(/\s+/g, ' ').trim()
}

function timeAgo(value: string): string {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000)
  if (!Number.isFinite(minutes)) return ''
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days} d ago`
  return new Date(value).toLocaleDateString()
}

async function openSidebar() {
  sidebarOpen.value = true
  document.body.style.overflow = 'hidden'
  await nextTick()
  drawerClose.value?.focus()
}

function closeSidebar(returnFocus = false) {
  if (!sidebarOpen.value) return
  sidebarOpen.value = false
  document.body.style.overflow = ''
  if (returnFocus) menuToggle.value?.focus()
}

function toggleMenu(menu: string) {
  openMenu.value = openMenu.value === menu ? null : menu
}

function closeMenu() {
  openMenu.value = null
}

function toggleNotificationPopup() {
  notificationPopupOpen.value = !notificationPopupOpen.value
  profileDropdownOpen.value = false
  if (notificationPopupOpen.value) notificationStore.fetchAll()
}

function closeNotificationPopup() {
  notificationPopupOpen.value = false
}

function toggleProfileDropdown() {
  profileDropdownOpen.value = !profileDropdownOpen.value
  notificationPopupOpen.value = false
}

function closeProfileDropdown() {
  profileDropdownOpen.value = false
}

function toggleSubmenu(menu: string) {
  activeSubmenu.value = activeSubmenu.value === menu ? null : menu
}

function handleSearch() {
  const term = searchQuery.value.trim()
  if (!term) return
  router.push({ name: 'search', query: { q: term } })
  searchQuery.value = ''
  closeSidebar()
}

async function handleLanguageChange(event: Event) {
  loadingLanguage.value = true
  try {
    await changeLanguage((event.target as HTMLSelectElement).value)
  } finally {
    loadingLanguage.value = false
  }
}

async function handleLogout() {
  closeProfileDropdown()
  closeSidebar()
  await authStore.logout()
}

// Opening a notification marks it read and shows the full list
async function openNotification(id: number, unread: boolean) {
  closeNotificationPopup()
  if (unread) await notificationStore.markAsRead(id)
  router.push('/notifications')
}

async function loadCountries() {
  try {
    loadingCountries.value = true
    const [packagesRes, visaRes] = await Promise.all([
      api.get('/package-countries'),
      api.get('/visa-countries')
    ])
    packageCountries.value = packagesRes.data.data || packagesRes.data || []
    visaCountries.value = visaRes.data.data || visaRes.data || []
    // Transport countries same as package countries
    transportCountries.value = packagesRes.data.data || packagesRes.data || []

    const cityLists = await Promise.all(
      packageCountries.value.map(country =>
        api.get(`/packages/cities/${country.id}`, { params: { with_packages: 1 } })
          .then(res => res.data.data || [])
          .catch(() => [])
      )
    )
    packageCities.value = Object.fromEntries(packageCountries.value.map((country, i) => [country.id, cityLists[i]]))
  } catch (error) {
    console.warn('Failed to load countries:', error)
  } finally {
    loadingCountries.value = false
  }
}

async function loadBalance() {
  try {
    loadingBalance.value = true
    const res = await api.get('/company/balance')
    availableBalance.value = res.data.data.balance || 0
  } catch (error) {
    console.warn('Failed to load balance:', error)
  } finally {
    loadingBalance.value = false
  }
}

async function loadNotificationCount() {
  try {
    await notificationStore.fetchLatest()
  } catch (error) {
    console.warn('Failed to load notifications:', error)
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  closeMenu()
  closeNotificationPopup()
  closeProfileDropdown()
  closeSidebar(true)
}

// Close popups when clicking outside them
function handleDocumentClick(e: MouseEvent) {
  const target = e.target as Node
  const notifications = document.querySelector('.hd-notify')
  if (notifications && !notifications.contains(target)) closeNotificationPopup()

  const profile = document.querySelector('.hd-profile')
  if (profile && !profile.contains(target)) closeProfileDropdown()

  const nav = document.querySelector('.hd-nav')
  if (nav && !nav.contains(target)) closeMenu()
}

// Close every menu after navigating
watch(() => route.fullPath, () => {
  closeMenu()
  closeProfileDropdown()
  closeNotificationPopup()
  closeSidebar()
  // The balance changes after a top-up, a payment or a booking
  loadBalance()
})

onMounted(() => {
  loadCountries()
  loadBalance()
  loadNotificationCount()
  initTranslator()

  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <header class="main-header hd">
    <!-- Top bar: logo, search, account tools -->
    <div class="hd-top">
      <div class="hd-inner">
        <button
          ref="menuToggle" type="button" class="hd-icon-btn hd-menu-toggle" aria-label="Open menu"
          aria-controls="hd-drawer" :aria-expanded="sidebarOpen" @click="openSidebar"
        >
          <i class="fa fa-bars" aria-hidden="true"></i>
        </button>

        <RouterLink to="/home" class="hd-logo" aria-label="China Tour Deal, dashboard">
          <img :src="logoImg" alt="China Tour Deal" />
        </RouterLink>

        <form class="hd-search" role="search" @submit.prevent="handleSearch">
          <i class="fa fa-search" aria-hidden="true"></i>
          <label class="visually-hidden" for="hd-search-input">Search packages</label>
          <input id="hd-search-input" v-model="searchQuery" type="search" autocomplete="off" placeholder="Search packages, cities, codes…" />
          <button type="submit" class="hd-search-btn">Search</button>
        </form>

        <div class="hd-tools">
          <!-- Language -->
          <div class="hd-lang" :class="{ 'is-busy': loadingLanguage }">
            <i class="fa fa-globe" aria-hidden="true"></i>
            <select
              :value="currentLanguage" :disabled="!translatorReady || loadingLanguage"
              aria-label="Language" title="Language" @change="handleLanguageChange"
            >
              <option value="en">EN</option>
              <option value="bn">BN</option>
            </select>
          </div>

          <!-- Google Translate (hidden but functional) -->
          <div id="google_translate_element"></div>

          <!-- Balance -->
          <RouterLink
            to="/finance" class="hd-balance" :class="availableBalance >= 0 ? 'is-good' : 'is-due'"
            :title="availableBalance >= 0 ? 'Available balance' : 'Amount due'"
          >
            <i class="fa fa-wallet" aria-hidden="true"></i>
            <span class="hd-balance-text">
              <span class="hd-balance-label">{{ availableBalance >= 0 ? 'Balance' : 'Due' }}</span>
              <span class="hd-balance-amount">{{ loadingBalance && !availableBalance ? '…' : formatAmount(Math.abs(availableBalance)) }}</span>
            </span>
          </RouterLink>

          <!-- Notifications -->
          <div class="hd-notify">
            <button
              type="button" class="hd-icon-btn hd-bell" :aria-expanded="notificationPopupOpen" aria-haspopup="true"
              :aria-label="notificationStore.unreadCount > 0 ? `Notifications, ${notificationStore.unreadCount} unread` : 'Notifications'"
              @click.stop="toggleNotificationPopup"
            >
              <i class="fa fa-bell" aria-hidden="true"></i>
              <span v-if="notificationStore.unreadCount > 0" class="hd-count" aria-hidden="true">
                {{ notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount }}
              </span>
            </button>

            <div v-if="notificationPopupOpen" class="hd-pop hd-notify-pop" @click.stop>
              <div class="hd-pop-head">
                <strong>Notifications</strong>
                <span v-if="notificationStore.unreadCount > 0" class="hd-pop-badge">{{ notificationStore.unreadCount }} unread</span>
              </div>
              <div class="hd-notify-list">
                <div v-if="notificationStore.loading && !latestNotifications.length" class="hd-notify-empty">
                  <span class="ui-spinner" aria-hidden="true"></span>Loading…
                </div>
                <div v-else-if="!latestNotifications.length" class="hd-notify-empty">
                  <i class="fa fa-bell-slash" aria-hidden="true"></i>No notifications yet
                </div>
                <button
                  v-for="notification in latestNotifications" v-else :key="notification.id" type="button"
                  class="hd-notify-item" :class="{ 'is-unread': notification.pivot && !notification.pivot.read_at }"
                  @click="openNotification(notification.id, !!(notification.pivot && !notification.pivot.read_at))"
                >
                  <span class="hd-notify-dot" aria-hidden="true"></span>
                  <span class="hd-notify-main">
                    <span class="hd-notify-title">{{ notification.title }}</span>
                    <span v-if="preview(notification.body)" class="hd-notify-body">{{ preview(notification.body) }}</span>
                    <span class="hd-notify-time">{{ timeAgo(notification.created_at) }}</span>
                  </span>
                  <span v-if="notification.pivot && !notification.pivot.read_at" class="visually-hidden">Unread</span>
                </button>
              </div>
              <RouterLink to="/notifications" class="hd-pop-foot">See all notifications <i class="fa fa-arrow-right" aria-hidden="true"></i></RouterLink>
            </div>
          </div>

          <!-- Account -->
          <div class="hd-profile">
            <button
              type="button" class="hd-profile-btn" :aria-expanded="profileDropdownOpen" aria-haspopup="true"
              @click.stop="toggleProfileDropdown"
            >
              <span class="hd-avatar" aria-hidden="true">{{ initials }}</span>
              <span class="hd-profile-text">
                <span class="hd-profile-name">{{ userName }}</span>
                <span class="hd-profile-company">{{ companyCode || companyName }}</span>
              </span>
              <i class="fa fa-chevron-down hd-caret" aria-hidden="true"></i>
            </button>

            <div v-if="profileDropdownOpen" class="hd-pop hd-profile-pop" @click.stop>
              <div class="hd-profile-card">
                <span class="hd-avatar is-lg" aria-hidden="true">{{ initials }}</span>
                <div>
                  <strong>{{ companyName || 'Your company' }}</strong>
                  <span>{{ userName }}<template v-if="companyCode"> · {{ companyCode }}</template></span>
                  <small>{{ authStore.user?.email }}</small>
                </div>
              </div>
              <div class="hd-pop-links">
                <RouterLink v-for="link in ACCOUNT_LINKS" :key="link.to" :to="link.to">
                  <i :class="link.icon" aria-hidden="true"></i>{{ link.label }}
                </RouterLink>
              </div>
              <button type="button" class="hd-signout" @click="handleLogout">
                <i class="fa fa-sign-out-alt" aria-hidden="true"></i>Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main navigation (desktop). A div, not a nav: the old theme styles every "header nav" as a drawer -->
    <div class="hd-nav" role="navigation" aria-label="Main">
      <div class="hd-inner">
        <ul class="hd-nav-list">
          <li
            v-for="item in NAV" :key="item.key" class="hd-nav-item"
            :class="{ 'is-open': openMenu === item.key }"
            @mouseenter="item.children && (openMenu = item.key)"
            @mouseleave="item.children && closeMenu()"
          >
            <RouterLink
              v-if="!item.children" :to="item.to!" class="hd-nav-link" :class="{ 'is-active': isActive(...item.match) }"
              :aria-current="isActive(...item.match) ? 'page' : undefined"
            >
              <i :class="item.icon" aria-hidden="true"></i>{{ item.label }}
            </RouterLink>
            <template v-else>
              <button
                type="button" class="hd-nav-link" :class="{ 'is-active': isActive(...item.match) }"
                :aria-expanded="openMenu === item.key" @click="toggleMenu(item.key)"
              >
                <i :class="item.icon" aria-hidden="true"></i>{{ item.label }}
                <i class="fa fa-chevron-down hd-caret" aria-hidden="true"></i>
              </button>
              <div class="hd-drop" :class="{ 'is-wide': item.wide }">
                <RouterLink v-if="item.allLink" :to="item.allLink.to" class="hd-drop-all">
                  {{ item.allLink.label }} <i class="fa fa-arrow-right" aria-hidden="true"></i>
                </RouterLink>
                <div class="hd-drop-grid">
                  <RouterLink v-for="child in item.children" :key="child.to" :to="child.to">{{ child.label }}</RouterLink>
                </div>
                <p v-if="item.allLink && !item.children?.length && loadingCountries" class="hd-drop-note">Loading countries…</p>
              </div>
            </template>
          </li>
        </ul>

        <RouterLink to="/package-requests/create" class="hd-cta">
          <i class="fa fa-plus" aria-hidden="true"></i><span>Request a package</span>
        </RouterLink>
      </div>
    </div>

    <!-- Mobile drawer -->
    <div
      id="hd-drawer" class="hd-drawer" :class="{ 'is-open': sidebarOpen }" role="dialog" aria-modal="true"
      aria-label="Menu" :aria-hidden="!sidebarOpen" :inert="!sidebarOpen || undefined"
    >
      <div class="hd-drawer-head">
        <RouterLink to="/home" class="hd-logo" @click="closeSidebar()">
          <img :src="logoImg" alt="China Tour Deal" />
        </RouterLink>
        <button ref="drawerClose" type="button" class="hd-icon-btn" aria-label="Close menu" @click="closeSidebar(true)">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <div class="hd-drawer-body">
        <div class="hd-profile-card">
          <span class="hd-avatar is-lg" aria-hidden="true">{{ initials }}</span>
          <div>
            <strong>{{ companyName || 'Your company' }}</strong>
            <span>{{ userName }}<template v-if="companyCode"> · {{ companyCode }}</template></span>
          </div>
        </div>

        <form class="hd-search is-drawer" role="search" @submit.prevent="handleSearch">
          <i class="fa fa-search" aria-hidden="true"></i>
          <label class="visually-hidden" for="hd-drawer-search">Search packages</label>
          <input id="hd-drawer-search" v-model="searchQuery" type="search" autocomplete="off" placeholder="Search packages…" />
        </form>

        <RouterLink to="/package-requests/create" class="hd-cta is-block">
          <i class="fa fa-plus" aria-hidden="true"></i>Request a package
        </RouterLink>

        <ul class="hd-drawer-list">
          <li v-for="item in NAV" :key="item.key">
            <RouterLink v-if="!item.children" :to="item.to!" class="hd-drawer-link" :class="{ 'is-active': isActive(...item.match) }">
              <i :class="item.icon" aria-hidden="true"></i>{{ item.label }}
            </RouterLink>
            <template v-else>
              <button
                type="button" class="hd-drawer-link" :class="{ 'is-active': isActive(...item.match) }"
                :aria-expanded="activeSubmenu === item.key" @click="toggleSubmenu(item.key)"
              >
                <i :class="item.icon" aria-hidden="true"></i>{{ item.label }}
                <i class="fa fa-chevron-down hd-caret" aria-hidden="true"></i>
              </button>
              <ul v-show="activeSubmenu === item.key" class="hd-drawer-sub">
                <li v-if="item.allLink"><RouterLink :to="item.allLink.to">{{ item.allLink.label }}</RouterLink></li>
                <li v-for="child in item.children" :key="child.to"><RouterLink :to="child.to">{{ child.label }}</RouterLink></li>
              </ul>
            </template>
          </li>
        </ul>

        <p class="hd-drawer-label">Account</p>
        <ul class="hd-drawer-list">
          <li v-for="link in ACCOUNT_LINKS" :key="link.to">
            <RouterLink :to="link.to" class="hd-drawer-link"><i :class="link.icon" aria-hidden="true"></i>{{ link.label }}</RouterLink>
          </li>
          <li>
            <button type="button" class="hd-drawer-link is-danger" @click="handleLogout">
              <i class="fa fa-sign-out-alt" aria-hidden="true"></i>Sign out
            </button>
          </li>
        </ul>

        <div class="hd-drawer-lang">
          <label for="hd-drawer-lang"><i class="fa fa-globe" aria-hidden="true"></i> Language</label>
          <div class="hd-lang" :class="{ 'is-busy': loadingLanguage }">
            <select id="hd-drawer-lang" :value="currentLanguage" :disabled="!translatorReady || loadingLanguage" @change="handleLanguageChange">
              <option value="en">English</option>
              <option value="bn">বাংলা</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="hd-overlay" :class="{ 'is-open': sidebarOpen }" aria-hidden="true" @click="closeSidebar(true)"></div>
  </header>

  <!-- <FirebaseChatWidget v-if="authStore.isAuthenticated" /> -->
</template>

<style scoped>
@import url("./AppHeader.css");
</style>

<style>
iframe.goog-te-banner-frame,
iframe.goog-te-banner-frame.skiptranslate,
body > .skiptranslate,
#goog-gt-tt,
.goog-tooltip,
.goog-text-highlight {
  display: none !important;
}

html,
body {
  top: 0 !important;
}
</style>
